import { useCallback } from 'react'
import { useSetRecoilState } from 'recoil'

import { addAlertQuery, AlertType } from 'services/alert'

import { useQrDecode } from './useQrDecode'
import { QrPasteZone } from './QrPasteZone'
import { QrSealInputs } from './QrSealInputs'
import { QrEntry } from './types'

import './qrpaste.style.css'

const extractImageFiles = (data: DataTransfer | null): File[] =>
  Array.from(data?.files ?? []).filter((file) => file.type.startsWith('image/'))

// Data URL содержимого файла: служит для превью и ключом для дедубликации одинаковых файлов.
const readDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

type Props = {
  entries: QrEntry[]
  onChange: (entries: QrEntry[]) => void
  // withSeal управляет отображением поля пломбы: не нужно там, где двигаются пустые контейнеры.
  withSeal?: boolean
}

export const QrPaste = ({ entries, onChange, withSeal = true }: Props) => {
  const { decodeImage, scannerElementId } = useQrDecode()
  const setAlert = useSetRecoilState(addAlertQuery)

  const handlePaste = useCallback(
    async (event: React.ClipboardEvent) => {
      const files = extractImageFiles(event.clipboardData)

      if (files.length === 0) return

      event.preventDefault()

      // Заполняем массив уже существующиими значениями, чтобы обнаружить дубли.
      const seenUrls = new Set(entries.map((entry) => entry.previewUrl))
      const decoded: QrEntry[] = []

      // Декодируем файлы до записи в стейт: нераспознанные не попадают в список и не "бликают"
      // при ренедер.
      for (const file of files) {
        const previewUrl = await readDataUrl(file)

        if (seenUrls.has(previewUrl)) {
          continue
        }

        seenUrls.add(previewUrl)

        const containerId = await decodeImage(file)

        if (!containerId) {
          setAlert([{ message: 'QR-код не распознан', type: AlertType.ERROR }])

          continue
        }

        decoded.push({ previewUrl, containerId, seal: '' })
      }

      if (decoded.length === 0) return

      onChange([...entries, ...decoded])
    },
    [decodeImage, setAlert, entries, onChange]
  )

  const handleEntryChange = useCallback(
    (containerId: string, newContainerId: string, seal: string) => {
      onChange(
        entries.map((entry) => {
          if (entry.containerId !== containerId) return entry

          return { ...entry, containerId: newContainerId, seal }
        })
      )
    },
    [entries, onChange]
  )

  const handleRemove = useCallback(
    (containerId: string) => {
      onChange(entries.filter((entry) => entry.containerId !== containerId))
    },
    [entries, onChange]
  )

  return (
    <div className="qr-paste">
      <QrPasteZone
        scannerElementId={scannerElementId}
        entries={entries}
        onPaste={handlePaste}
        onRemove={handleRemove}
      />
      <QrSealInputs
        entries={entries}
        onChange={handleEntryChange}
        onRemove={handleRemove}
        withSeal={withSeal}
      />
    </div>
  )
}
